import {
  restaurantOrderResponseSchema,
  weekdayOrderCountSchema,
  type MerchantDecision,
  type MerchantOrderFilter,
  type OrderListResult,
  type PaginationParams,
  type PreparationStage,
  type RestaurantOrder,
  type WeekdayOrderCount,
} from '../model';
import { getFlatList, getOne, getOrderList, postOne, type RequestOptions } from './http';

// Order Service — the merchant's inbound orders and the two transitions it
// owns: the accept/reject decision, and the kitchen preparation stage.
//
// Orders are listed either across the whole merchant (`by-merchant`) or for one
// branch (`by-restaurant`). Both take the same filters.

/** Filters shared by the two order list endpoints. */
export interface ListOrdersParams extends PaginationParams {
  merchant_id?: string;
  search?: string;
  /** RFC3339 range bounds, e.g. `2026-07-01T00:00:00Z`. */
  created_from?: string;
  created_to?: string;
  filter?: MerchantOrderFilter;
}

/** A date range, required by the weekday-count endpoints. */
export interface OrderDateRange {
  created_from: string;
  created_to: string;
}

export const orderApi = {
  /** List every order across the merchant's branches. */
  listByMerchant(
    params?: ListOrdersParams,
    options?: RequestOptions,
  ): Promise<OrderListResult> {
    return getOrderList('orders/by-merchant', { ...params }, options);
  },

  /** List one branch's orders. */
  listByRestaurant(
    restaurantId: string,
    params?: ListOrdersParams,
    options?: RequestOptions,
  ): Promise<OrderListResult> {
    return getOrderList(
      'merchant/orders/by-restaurant',
      { ...params, restaurant_id: restaurantId },
      options,
    );
  },

  /** Get a single order by id. */
  async get(
    id: string,
    restaurantId?: string,
    options?: RequestOptions,
  ): Promise<RestaurantOrder> {
    const { order } = await getOne(
      `merchant/orders/by-restaurant/${id}`,
      restaurantOrderResponseSchema,
      { restaurant_id: restaurantId },
      options,
    );
    return order;
  },

  /**
   * Orders per weekday across the merchant. The date range is REQUIRED — the
   * backend rejects a call without it (`created_from`/`created_to` violations).
   */
  async weekdayCountsByMerchant(
    range: OrderDateRange,
    options?: RequestOptions,
  ): Promise<WeekdayOrderCount[]> {
    const { list } = await getFlatList(
      'merchant/orders/weekday-counts',
      weekdayOrderCountSchema,
      { ...range },
      options,
    );
    return list;
  },

  /** Orders per weekday for one branch. The date range is REQUIRED. */
  async weekdayCountsByRestaurant(
    restaurantId: string,
    range: OrderDateRange,
    options?: RequestOptions,
  ): Promise<WeekdayOrderCount[]> {
    const { list } = await getFlatList(
      'merchant/orders/by-restaurant/weekday-counts',
      weekdayOrderCountSchema,
      { restaurant_id: restaurantId, ...range },
      options,
    );
    return list;
  },

  /** Accept or reject a pending order, optionally specifying pickup_delta_minutes. */
  async respond(
    orderId: string,
    decision: MerchantDecision,
    pickupDeltaMinutes?: number,
  ): Promise<RestaurantOrder> {
    const { order } = await postOne(
      `merchant/orders/${orderId}/merchant-decision`,
      {
        decision,
        ...(pickupDeltaMinutes !== undefined
          ? { pickup_delta_minutes: pickupDeltaMinutes }
          : {}),
      },
      restaurantOrderResponseSchema,
    );
    return order;
  },

  /** Cancel an order by restaurant (`POST /merchant/orders/{id}/restaurant-cancel`). */
  async cancelByRestaurant(
    orderId: string,
    options?: RequestOptions,
  ): Promise<RestaurantOrder> {
    const { order } = await postOne(
      `merchant/orders/${orderId}/restaurant-cancel`,
      {},
      restaurantOrderResponseSchema,
      options,
    );
    return order;
  },

  /**
   * Advance an accepted order's kitchen stage. `READY` → `PICKED_UP` for a
   * courier order; a PICKUP order finishes with `HANDED_TO_CUSTOMER` instead
   * (v1.00.227) — the only stage that moves самовывоз to `delivered`.
   */
  async updatePreparationStage(
    orderId: string,
    stage: PreparationStage,
  ): Promise<RestaurantOrder> {
    const { order } = await postOne(
      `merchant/orders/${orderId}/preparation-status`,
      { stage },
      restaurantOrderResponseSchema,
    );
    return order;
  },

  /**
   * Move the courier's arrival time by `deltaMinutes` minutes via `POST /merchant/orders/{id}/merchant-decision`.
   * Note: `POST /orders/{id}/courier-pickup-time` was removed in favor of `pickup_delta_minutes`
   * on `POST /merchant/orders/{id}/merchant-decision`.
   */
  async adjustCourierPickupTime(
    orderId: string,
    deltaMinutes: number,
  ): Promise<RestaurantOrder> {
    return this.respond(orderId, 'MERCHANT_DECISION_ACCEPTED', deltaMinutes);
  },
};
